/**
 * Sound class for handling audio playback using the
 * [Web Audio API](https://developer.mozilla.org/en-US/docs/Web/API/Web_Audio_API).
 *
 * This class provides a more reliable way to play multiple, rapid-fire sound
 * effects, which is not possible in Safari when using the HTMLAudioElement.
 *
 * @example
 * ```ts
 * const soundEffect = new Sound("/sounds/subtle.mp3");
 * await soundEffect.load();
 * soundEffect.play();
 * ```
 *
 * Approach adapted from https://stackoverflow.com/a/76916863
 */
export class Sound {
  context = new AudioContext();
  url: string = "";
  buffer: AudioBuffer | null = null;
  sources: Array<AudioBufferSourceNode> = [];

  constructor (url: string) {
    this.url = url;
  }

  load (): Promise<AudioBuffer> {
    if (!this.url) return Promise.reject(new Error(`Missing or invalid URL: ${this.url}`));
    if (this.buffer) return Promise.resolve(this.buffer);

    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("GET", this.url, true);
      request.responseType = "arraybuffer";

      request.onload = () => {
        this.context.decodeAudioData(request.response, (buffer) => {
          if (!buffer) {
            reject(new Error(`Sound decoding error: ${this.url}`));
            return;
          }

          this.buffer = buffer;
          resolve(buffer);
        });
      };

      request.onerror = (err) => {
        reject(err);
      };

      request.send();
    });
  }

  play (options: {
    volume?: number,
    time?: number,
  } = {}) {
    const {
      volume = 1,
      time = 0,
    } = options;

    if (!this.buffer) return;

    const source = this.context.createBufferSource();
    source.buffer = this.buffer;

    const insertedAt = this.sources.push(source) - 1;

    source.onended = () => {
      source.stop(0);
      this.sources.splice(insertedAt, 1);
    };

    const gainNode = this.context.createGain();
    gainNode.gain.value = volume;

    source.connect(gainNode).connect(this.context.destination);
    source.start(time);
  }

  stop () {
    this.sources.forEach((source) => {
      source.stop(0);
    });

    this.sources = [];
  }
}
